import {
  AlertDialog, AlertDialogBody, AlertDialogFooter,
  AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  useDisclosure,
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiMail, FiCalendar } from "react-icons/fi";
import { validateFullName } from '../utils/validators';
import Select from 'react-select';
import { Country, City } from 'country-state-city';
import { FiLogOut, FiTrash2 } from "react-icons/fi";
import Navbar from "../components/Navbar";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, fetchWithAuth } from "../context/AuthContext";

const FIELD_OPTIONS = [
  { value: 'mechanical_engineering', label: 'Mechanical Engineering' },
  { value: 'civil_engineering', label: 'Civil Engineering' },
  { value: 'production_engineering', label: 'Production Engineering' },
  { value: 'electrical_engineering', label: 'Electrical Engineering' },
  { value: 'robotics_engineering', label: 'Robotics Engineering' },
  { value: 'designer', label: 'Designer' },
  { value: 'art_consultant', label: 'Art Consultant' },
  { value: 'fabricator', label: 'Fabricator' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_OPTIONS = Country.getAllCountries().map(c => ({
  value: c.isoCode,
  label: c.name,
}));

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  // Prefer env-configured API URL, fall back to local dev
  const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [nameError, setNameError] = useState('');
  const [profileErrors, setProfileErrors] = useState({});
  const [cityOptions, setCityOptions] = useState([]);
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    country: null,
    city: null,
    field: null,
    fieldOther: '',
  });
  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();
  const cancelLogoutRef = useRef();

  // Populate form from auth user once loaded
  useEffect(() => {
    if (user) {
      const countryObj = user.country
        ? COUNTRY_OPTIONS.find(c => c.label === user.country) || null
        : null;

      const fieldObj = user.field
        ? FIELD_OPTIONS.find(f => f.value === user.field) || null
        : user.fieldOther
          ? { value: 'other', label: 'Other' }
          : null;

      if (countryObj) {
        const cities = City.getCitiesOfCountry(countryObj.value).map(c => ({
          value: c.name, label: c.name,
        }));
        setCityOptions(cities);
      }

      const cityObj = user.region
        ? { value: user.region, label: user.region }
        : null;

      setProfile({
        fullName: user.fullName || '',
        email: user.email || '',
        country: countryObj,
        city: cityObj,
        field: fieldObj,
        fieldOther: user.fieldOther || '',
      });
    }
  }, [user]);

  const handleCountryChange = (selected) => {
    setProfile(p => ({ ...p, country: selected, city: null }));
    if (selected) {
      const cities = City.getCitiesOfCountry(selected.value).map(c => ({
        value: c.name, label: c.name,
      }));
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  };

  const handleSaveProfile = async () => {
    const nameErr = validateFullName(profile.fullName);
    if (nameErr) { setNameError(nameErr); return; }
    // ── Empty field validation for all other fields ──
    const errors = {};
    if (!profile.field) errors.field = 'Your field is required';
    if (profile.field?.value === 'other' && !profile.fieldOther?.trim())
      errors.fieldOther = 'Please specify your field';
    if (!profile.country) errors.country = 'Country is required';
    if (!profile.city) errors.city = 'City is required';

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setProfileErrors({});

    try {
      const res = await fetchWithAuth(`${API_URL}/api/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: profile.fullName,
          country: profile.country?.label || null,
          region: profile.city?.value || null,
          field: profile.field?.value === 'other' ? null : profile.field?.value || null,
          fieldOther: profile.field?.value === 'other' ? profile.fieldOther : null,
        })
      });

      if (!res.ok) throw new Error('Failed to save');

      const updated = await res.json();
      setUser(prev => ({
        ...prev,
        fullName: updated.fullName,
        country: updated.country,
        region: updated.region,
        field: updated.field,
        fieldOther: updated.fieldOther,
      }));

      setIsEditingProfile(false);
    } catch (err) {
      console.error('Save profile error:', err);
      // optionally show a toast error
    }
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setNameError('');
    setProfileErrors({});
    // re-trigger useEffect by just calling it manually
    const countryObj = user?.country
      ? COUNTRY_OPTIONS.find(c => c.label === user.country) || null
      : null;
    const fieldObj = user?.field
      ? FIELD_OPTIONS.find(f => f.value === user.field) || null
      : user?.fieldOther ? { value: 'other', label: 'Other' } : null;

    setProfile({
      fullName: user?.fullName || '',
      email: user?.email || '',
      country: countryObj,
      city: user?.region ? { value: user.region, label: user.region } : null,
      field: fieldObj,
      fieldOther: user?.fieldOther || '',
    });
  };

  const selectStyles = (hasError = false) => ({
    control: (base, state) => ({
      ...base,
      borderRadius: '12px',
      minHeight: '44px',
      fontSize: '14px',
      backgroundColor: !isEditingProfile ? '#FAFAFA' : 'white',
      pointerEvents: !isEditingProfile ? 'none' : 'auto',
      borderColor: !isEditingProfile
        ? '#ECECEC'
        : hasError
          ? '#FC8181'
          : state.isFocused
            ? '#CF008A'
            : '#E0E0E0',
      boxShadow: !isEditingProfile
        ? 'none'
        : state.isFocused
          ? '0 0 0 1px #CF008A'
          : 'none',
      '&:hover': {
        borderColor: !isEditingProfile ? '#ECECEC' : '#D0D0D0',
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1A1A1A', // ✅ fixes greyed out text
    }),
    placeholder: (base) => ({
      ...base,
      color: !isEditingProfile ? '#C0C0C0' : '#9E9E9E',
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#CF008A'
        : state.isFocused
          ? '#FDF0F8'
          : 'white',
      color: state.isSelected ? 'white' : '#1A1A1A',
      '&:active': {
        backgroundColor: '#f0007e22',
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      opacity: !isEditingProfile ? 0 : 1, // ✅ hides dropdown arrow when read-only
    }),
  });

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  return (
    <Box minH="100vh" bg="#FFFFFF">
      <Navbar />

      <Box
        pt="clamp(80px, 9vh, 96px)"
        pb="clamp(32px, 4vh, 48px)"
        px="clamp(16px, 3vw, 40px)"
      >
        <Box maxW="1200px" mx="auto">
          <Flex align="center" gap="clamp(12px, 1.2vw, 16px)" mb="clamp(20px, 2vh, 28px)">
            <IconButton
              onClick={() => navigate(-1)}
              aria-label="Back"
              icon={<ArrowBackIcon boxSize="clamp(18px, 1.4vw, 22px)" />}
              variant="ghost"
              size="md"
              borderRadius="full"
              color="#242424"
              _hover={{ bg: "#F5F5F5", color: "#CF008A" }}
            />
            <Text
              fontSize="clamp(22px, 2vw, 28px)"
              fontWeight="semibold"
              color="#242424"
            >
              My Profile
            </Text>
          </Flex>

          <Flex
            gap="clamp(24px, 3vw, 40px)"
            align="flex-start"
            direction={{ base: "column", lg: "row" }}
          >
            {/* Left profile card */}
            <Box
              w={{ base: "100%", lg: "320px" }}
              bg="white"
              border="1px solid #ECECEC"
              borderRadius="20px"
              p="clamp(20px, 2vw, 24px)"
              boxShadow="sm"
            >
              <VStack align="stretch" spacing="clamp(16px, 1.5vw, 20px)">
                <VStack spacing="8px" align="center">
                  <Box
                    w="88px"
                    h="88px"
                    borderRadius="full"
                    bgGradient="linear(to-br, #FFD6F6, #CDEBFF)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Avatar
                      name={user.fullName}
                      src={user?.avatarUrl || undefined}
                      size="xl"
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      bg="#CF008A"
                      color="white"
                    />
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="16px" fontWeight="semibold" color="#242424">
                      {user.fullName || '—'}
                    </Text>
                    <Text fontSize="14px" color="#6D6D6D">
                      {profile.field?.label || 'Member'}
                    </Text>
                  </Box>
                </VStack>

                <VStack align="stretch" spacing="10px" fontSize="14px" color="#4F4F4F">
                  {/* <HStack spacing="10px">
                    <Box as={FiMail} color="#6D6D6D" />
                    <Text>{profile.email}</Text>
                  </HStack>
                   */}
                  <HStack spacing="10px">
                    <Box as={FiCalendar} color="#6D6D6D" />
                    <Text>Joined on {user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}</Text>
                  </HStack>
                </VStack>
              </VStack>
            </Box>

            {/* Right content */}
            <VStack flex="1" align="stretch" spacing="clamp(24px, 2.5vw, 32px)">
              {/* My Profile section */}
              <Box>
                <Flex
                  align="center"
                  justify="space-between"
                  mb="clamp(12px, 1vh, 16px)"
                >
                  <Text fontSize="16px" fontWeight="bold">
                    My Profile
                  </Text>
                  {!isEditingProfile ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="pink"
                      borderColor="#CF008A"
                      color="#CF008A"
                      fontWeight="semibold"
                      borderRadius="10px"
                      _hover={{ bg: "#FFF0FA" }}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <HStack spacing="8px">
                      <Button
                        size="sm"
                        variant="ghost"
                        color="#6D6D6D"
                        fontWeight="semibold"
                        borderRadius="10px"
                        _hover={{ bg: "#F5F5F5" }}
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        bg="#CF008A"
                        color="white"
                        fontWeight="semibold"
                        borderRadius="10px"
                        _hover={{ bg: "#D933A1" }}
                        onClick={handleSaveProfile}
                      >
                        Save
                      </Button>
                    </HStack>
                  )}
                </Flex>
                <VStack
                  align="stretch"
                  spacing="clamp(16px, 1.5vw, 20px)"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <FormControl isInvalid={!!nameError}>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Name
                    </FormLabel>
                    <Input
                      value={profile.fullName}
                      onChange={(e) => {
                        setProfile((p) => ({ ...p, fullName: e.target.value }));
                        if (isEditingProfile) setNameError('');
                      }}
                      onBlur={() => {
                        if (isEditingProfile) setNameError(validateFullName(profile.fullName));
                      }}
                      readOnly={!isEditingProfile}
                      tabIndex={isEditingProfile ? 0 : -1}
                      h="44px"
                      borderRadius="12px"
                      border={`1px solid ${nameError ? '#FC8181' : '#E0E0E0'}`}
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={
                        isEditingProfile
                          ? { borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }
                          : { borderColor: "#ECECEC", boxShadow: "none" }
                      }
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                    {nameError && <Text fontSize="xs" color="red.500" mt={1}>{nameError}</Text>}
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Bio
                    </FormLabel>
                    <Textarea
                      value={profile.bio}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, bio: e.target.value }))
                      }
                      readOnly={!isEditingProfile}
                      minH="100px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={{
                        borderColor: "#CF008A",
                        boxShadow: "0 0 0 1px #CF008A",
                      }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl> */}

                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Email address
                    </FormLabel>
                    <Input
                      type="email"
                      readOnly={true}
                      tabIndex={-1}
                      value={profile.email}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, email: e.target.value }))
                      }
                      h="44px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        // isEditingProfile
                        //   ? { borderColor: "#D0D0D0" }
                        //   : 
                        undefined
                      }
                      _focus={{ borderColor: "#ECECEC", boxShadow: "none" }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl>

                  {/* Field / Role */}
                  <FormControl isInvalid={!!profileErrors.field}>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Field
                    </FormLabel>
                    <Select
                      options={FIELD_OPTIONS}
                      value={profile.field}
                      onChange={val => {
                        setProfile(p => ({ ...p, field: val, fieldOther: '' }));
                        setProfileErrors(p => ({ ...p, field: '', fieldOther: '' }));
                      }} placeholder="Select your field..."
                      styles={selectStyles(!!profileErrors.field)}
                      isSearchable
                      isDisabled={!isEditingProfile}
                    />
                    {profileErrors.field && (
                      <Text fontSize="xs" color="red.500" mt={1}>{profileErrors.field}</Text>
                    )}
                    {profile.field?.value === 'other' && (
                      <>
                        <Input
                          mt={2}
                          placeholder="Please specify your field"
                          value={profile.fieldOther}
                          onChange={e => {
                            setProfile(p => ({ ...p, fieldOther: e.target.value }));
                            if (profileErrors.fieldOther) setProfileErrors(p => ({ ...p, fieldOther: '' }));
                          }}
                          readOnly={!isEditingProfile}
                          tabIndex={isEditingProfile ? 0 : -1}
                          h="44px"
                          borderRadius="12px"
                          border={`1px solid ${profileErrors.fieldOther ? '#FC8181' : '#E0E0E0'}`}
                          _focus={
                            isEditingProfile
                              ? { borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }
                              : { borderColor: '#ECECEC', boxShadow: 'none' }
                          }
                          _readOnly={{ bg: '#FAFAFA', cursor: 'default', borderColor: '#ECECEC' }}
                        />
                        {profileErrors.fieldOther && (
                          <Text fontSize="xs" color="red.500" mt={1}>{profileErrors.fieldOther}</Text>
                        )}
                      </>
                    )}
                  </FormControl>

                  {/* City */}
                  <FormControl isInvalid={!!profileErrors.city}>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      City
                    </FormLabel>
                    <Select
                      options={cityOptions}
                      value={profile.city}
                      placeholder={profile.country ? 'Select city...' : 'Select country first'}
                      styles={selectStyles(!!profileErrors.city)}
                      onChange={val => {
                        setProfile(p => ({ ...p, city: val }));
                        setProfileErrors(p => ({ ...p, city: '' }));
                      }}
                      isSearchable
                      isDisabled={!isEditingProfile || !profile.country}
                    />
                    {profileErrors.city && <Text fontSize="xs" color="red.500" mt={1}>{profileErrors.city}</Text>}
                  </FormControl>

                  {/* Country */}
                  <FormControl isInvalid={!!profileErrors.country}>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Country
                    </FormLabel>
                    <Select
                      options={COUNTRY_OPTIONS}
                      value={profile.country}
                      onChange={selected => {
                        handleCountryChange(selected);
                        setProfileErrors(p => ({ ...p, country: '', city: '' }));
                      }}
                      placeholder="Select country..."
                      styles={selectStyles(!!profileErrors.country)}
                      isSearchable
                      isDisabled={!isEditingProfile}
                    />
                    {profileErrors.country && <Text fontSize="xs" color="red.500" mt={1}>{profileErrors.country}</Text>}
                  </FormControl>

                </VStack>
              </Box>

              {/* Delete account */}
              {/* <Box>
                <Text
                  fontSize="16px"
                  fontWeight="bold"
                  mb="clamp(12px, 1vh, 16px)"
                >
                  Delete account
                </Text>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap="clamp(12px, 1.5vw, 16px)"
                  align={{ base: "stretch", md: "center" }}
                  justify="space-between"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <Text fontSize="14px" color="#6D6D6D" maxW="360px">
                    Deleting your account will permanently remove all your data and cannot be undone.
                  </Text>
                  <HStack spacing="10px">
                    <Button
                      bg="#DE2424"
                      _hover={{ bg: "#F04747" }}
                      color="white"
                      borderRadius="12px"
                      h="44px"
                      px="clamp(16px, 1.8vw, 24px)"
                      leftIcon={<FiLogOut size={16} />}
                      onClick={handleLogout}
                    >
                      Log out
                    </Button>
                    <Button
                      leftIcon={<FiTrash2 size={16} />}
                      variant="outline"
                      borderColor="#FF5A5A"
                      color="#DE2424"
                      borderRadius="12px"
                      h="44px"
                      px="clamp(16px, 1.8vw, 24px)"
                      _hover={{ bg: "#FFF5F5" }}
                    >
                      Delete account
                    </Button>
                  </HStack>
                </Flex>
              </Box> */}

              {/* Logout */}
              <Box>
                <Text fontSize="16px" fontWeight="bold" mb="clamp(12px, 1vh, 16px)">
                  Session
                </Text>
                <Flex
                  align="center"
                  justify="space-between"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <Box>
                    <Text fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Log out of your account
                    </Text>
                    <Text fontSize="14px" color="#6D6D6D" mt="2px">
                      You can always log back in anytime.
                    </Text>
                  </Box>
                  <Button
                    leftIcon={<FiLogOut size={16} />}
                    variant="outline"
                    borderColor="#CF008A"
                    color="#CF008A"
                    borderRadius="12px"
                    h="44px"
                    px="clamp(16px, 1.8vw, 24px)"
                    _hover={{ bg: "#FDF0F8" }}
                    onClick={onLogoutOpen}
                  >
                    Log out
                  </Button>
                </Flex>
              </Box>
            </VStack>
          </Flex>
        </Box>
      </Box>
      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={cancelLogoutRef}
        onClose={onLogoutClose}
        isCentered
      >
        <AlertDialogOverlay backdropFilter="blur(4px)" />
        <AlertDialogContent borderRadius="16px" p={2}>
          <AlertDialogHeader fontSize="18px" fontWeight="bold" color="#1A1A1A">
            Log out
          </AlertDialogHeader>
          <AlertDialogBody fontSize="14px" color="#6D6D6D">
            Are you sure you want to log out of your account?
          </AlertDialogBody>
          <AlertDialogFooter gap="8px">
            <Button
              ref={cancelLogoutRef}
              variant="ghost"
              color="#6D6D6D"
              borderRadius="10px"
              bg='#F5F5F5'
              _hover={{ bg: 'white' }}
              onClick={async () => { onLogoutClose(); await handleLogout(); }}
            >
              Yes, log out
            </Button>
            <Button
              bg="#CF008A"
              color="white"
              borderRadius="10px"
              _hover={{ bg: '#D933A1' }}
              onClick={onLogoutClose}
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
}

